// S9.2 W9-08: whale-flow `timestamp` is an age LABEL, not a time. v2
// app/flow_age.py writes one of: "just now", "{N}m ago", "{N}h ago",
// "{N}d ago" (the days bucket arrives with S9.2 G10; until then, and for one
// cache period after, hour labels such as "14199h ago" are still served) or
// "unknown age". The parser mirrors v2's _LABEL_RE fullmatch; anything else
// (null, a legacy string) is treated as an unknown age.

const LABEL = /^(\d+)([mhd]) ago$/
const UNIT_SECONDS: Record<string, number> = { m: 60, h: 3600, d: 86400 }
const DAY_SECONDS = 86400

/** Seconds of age a label stands for, or null when the age is unknown. */
export function flowAgeSeconds(label: unknown): number | null {
  if (label === 'just now') return 0
  if (typeof label !== 'string') return null
  const m = LABEL.exec(label)
  return m ? Number(m[1]) * UNIT_SECONDS[m[2]] : null
}

/**
 * A notice for the listed flows when even the newest of them is at least a
 * day old; null otherwise. Only the listed rows are measured (the homepage
 * lists the top flows by USD, not the newest), so the copy says "listed here".
 * Rows with an unknown age could be newer, so when any is present the notice
 * is limited to the rows whose age is known. No known age → no notice.
 */
export function staleFlowNotice(labels: unknown[]): string | null {
  const ages = labels.map(flowAgeSeconds)
  const known = ages.filter((a): a is number => a !== null)
  if (known.length === 0) return null
  const newest = Math.min(...known)
  if (newest < DAY_SECONDS) return null
  const days = Math.floor(newest / DAY_SECONDS)
  const age = `${days} ${days === 1 ? 'day' : 'days'} old`
  return known.length < ages.length
    ? `The newest flow listed here with a known age is ${age}.`
    : `The newest flow listed here is ${age}.`
}
